import { useContext, useMemo, useEffect, useState, useRef } from "react";
import { CartContext } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { host } from "../config/Config";
import "../pages/educations/style.css";
import "./CartPage.css";
import Logo from "../resources/logo.png";
import eduImage from "../resources/edu.jpeg";
import fundamentalImg from "../resources/fundamental.jpg";
import formacijeImg from "../resources/sth_1.jpg";
import kreativnaImg from "../resources/sth_2.jpg";
import fadeImg from "../resources/sth_3.jpg";
import beginnerImg from "../resources/img-2.jpeg";
import handsOnImg from "../resources/hands_on.jpg";
import lookAndLearnImg from "../resources/look_and_learn.jpg";
import oneOnOneImg from "../resources/one_on_one.jpg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/sr";

const formatDateManual = (dateString) => {
    const months = [
        "jan",
        "feb",
        "mar",
        "apr",
        "maj",
        "jun",
        "jul",
        "avg",
        "sep",
        "okt",
        "nov",
        "dec",
    ];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    return `${day} ${month}`;
};

const getEducationImage = (educationType) => {
    const imageMap = {
        fundamental: fundamentalImg,
        formation: formacijeImg,
        "creative-formation": kreativnaImg,
        fade: fadeImg,
        "nine-week": beginnerImg,
        "twelve-week": beginnerImg,
        "hands-on": handsOnImg,
        "look-and-learn": lookAndLearnImg,
        "one-on-one": oneOnOneImg,
    };
    return imageMap[educationType] || eduImage;
};

const CartPage = () => {
    const { cartItems, removeFromCart } = useContext(CartContext);
    const navigate = useNavigate();
    const [selectedSlots, setSelectedSlots] = useState({});
    const [selectedStartDates, setSelectedStartDates] = useState({});
    const [showValidation, setShowValidation] = useState(false);
    const [bundlePrices, setBundlePrices] = useState({});
    const [educationSlots, setEducationSlots] = useState({});
    const isInitialMount = useRef(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Redirect if cart is empty only on initial page load
        if (isInitialMount.current && (!cartItems || cartItems.length === 0)) {
            navigate("/educations");
        }
        isInitialMount.current = false;

        // Fetch bundle prices
        fetch(`${host}/api/v1/education-prices`)
            .then((response) => response.json())
            .then((data) => {
                const bundles = {};
                data.forEach((item) => {
                    if (item.educationType.endsWith("-bundle")) {
                        bundles[item.educationType] = item.price;
                    }
                });
                setBundlePrices(bundles);
            })
            .catch((error) =>
                console.error("Error fetching bundle prices:", error)
            );

        // Fetch education slots for each cart item
        if (cartItems && cartItems.length > 0) {
            cartItems.forEach((item) => {
                // Only fetch slots for workshop educations and single educations that are NOT one-on-one
                const needsSlot =
                    item.category === "workshop" ||
                    (item.category === "single" && item.id !== "one-on-one");
                if (needsSlot && !educationSlots[item.id]) {
                    fetch(`${host}/api/v1/education-slots/type/${item.id}`)
                        .then((response) => response.json())
                        .then((slots) => {
                            const activeSlots = slots.filter(
                                (slot) =>
                                    slot.status === "ACTIVE" &&
                                    slot.availableSlots > 0
                            );
                            setEducationSlots((prev) => ({
                                ...prev,
                                [item.id]: activeSlots,
                            }));

                            // Auto-select if only one slot available
                            if (activeSlots.length === 1) {
                                setSelectedSlots((prevSelected) => ({
                                    ...prevSelected,
                                    [item.id]: activeSlots[0].id,
                                }));
                            }
                        })
                        .catch((error) =>
                            console.error(
                                `Error fetching slots for ${item.id}:`,
                                error
                            )
                        );
                }
            });
        }
    }, [cartItems, navigate]);

    const handleSlotSelect = (itemId, slotId) => {
        setSelectedSlots((prev) => ({
            ...prev,
            [itemId]: slotId,
        }));
    };

    const handleStartDateSelect = (itemId, date) => {
        setSelectedStartDates((prev) => ({
            ...prev,
            [itemId]: date,
        }));
    };

    const needsAppointment = (category, itemId) => {
        // Workshop educations need slots
        if (category === "workshop") return true;
        // One-on-one needs date/time picker (not slots)
        if (category === "single" && itemId !== "one-on-one") return true;
        return false;
    };

    const needsStartDate = (category, itemId) => {
        return category === "beginner" || itemId === "one-on-one";
    };

    const getMinStartDate = (itemId) => {
        // One-on-one requires 10 days minimum
        if (itemId === "one-on-one") {
            return dayjs().add(10, "day");
        }
        // Beginner educations also require 10 days
        return dayjs().add(10, "day");
    };

    const shouldDisableDate = (date, itemId) => {
        // Disable Sundays for one-on-one
        if (itemId === "one-on-one" && date.day() === 0) {
            return true;
        }
        return false;
    };

    const { subtotal, discount, total, discountInfo, discountPercent } =
        useMemo(() => {
            if (!cartItems || cartItems.length === 0) {
                return {
                    subtotal: 0,
                    discount: 0,
                    total: 0,
                    discountInfo: null,
                    discountPercent: 0,
                };
            }

            const workshopItems = cartItems.filter(
                (item) => item.category === "workshop"
            );
            const singleItems = cartItems.filter(
                (item) => item.category === "single"
            );
            const beginnerItems = cartItems.filter(
                (item) => item.category === "beginner"
            );
            const onlineItems = cartItems.filter(
                (item) => item.category === "online"
            );

            // All education IDs by category
            const allWorkshopIds = [
                "fundamental",
                "formation",
                "creative-formation",
                "fade",
            ];
            const allSingleIds = ["hands-on", "look-and-learn", "one-on-one"];
            const allBeginnerIds = ["nine-week", "twelve-week"];
            const allOnlineIds = ["online"];

            const hasAllWorkshops = allWorkshopIds.every((id) =>
                workshopItems.some((item) => item.id === id)
            );
            const hasAllSingles = allSingleIds.every((id) =>
                singleItems.some((item) => item.id === id)
            );
            const hasAllBeginners = allBeginnerIds.every((id) =>
                beginnerItems.some((item) => item.id === id)
            );
            const hasAllOnline = allOnlineIds.every((id) =>
                onlineItems.some((item) => item.id === id)
            );

            const subtotal = cartItems.reduce(
                (sum, item) => sum + item.price,
                0
            );
            let discount = 0;
            let discountInfo = null;
            let discountPercent = 0;

            // Apply bundle discounts based on fetched prices
            if (hasAllWorkshops && bundlePrices["workshop-bundle"]) {
                const workshopSubtotal = workshopItems.reduce(
                    (sum, item) => sum + item.price,
                    0
                );
                const bundlePrice = bundlePrices["workshop-bundle"];
                const workshopDiscount = workshopSubtotal - bundlePrice;
                if (workshopDiscount > 0) {
                    discount += workshopDiscount;
                    discountPercent = Math.round(
                        (workshopDiscount / workshopSubtotal) * 100
                    );
                    discountInfo = `${discountPercent}% popust - Sve Workshop edukacije`;
                }
            }

            if (hasAllSingles && bundlePrices["single-bundle"]) {
                const singleSubtotal = singleItems.reduce(
                    (sum, item) => sum + item.price,
                    0
                );
                const bundlePrice = bundlePrices["single-bundle"];
                const singleDiscount = singleSubtotal - bundlePrice;
                if (singleDiscount > 0) {
                    discount += singleDiscount;
                    const singlePercent = Math.round(
                        (singleDiscount / singleSubtotal) * 100
                    );
                    discountInfo = discountInfo
                        ? `${discountInfo} + ${singlePercent}% popust - Sve jednodnevne edukacije`
                        : `${singlePercent}% popust - Sve jednodnevne edukacije`;
                }
            }

            if (hasAllBeginners && bundlePrices["beginner-bundle"]) {
                const beginnerSubtotal = beginnerItems.reduce(
                    (sum, item) => sum + item.price,
                    0
                );
                const bundlePrice = bundlePrices["beginner-bundle"];
                const beginnerDiscount = beginnerSubtotal - bundlePrice;
                if (beginnerDiscount > 0) {
                    discount += beginnerDiscount;
                    const beginnerPercent = Math.round(
                        (beginnerDiscount / beginnerSubtotal) * 100
                    );
                    discountInfo = discountInfo
                        ? `${discountInfo} + ${beginnerPercent}% popust - Sve početničke edukacije`
                        : `${beginnerPercent}% popust - Sve početničke edukacije`;
                }
            }

            if (hasAllOnline && bundlePrices["online-bundle"]) {
                const onlineSubtotal = onlineItems.reduce(
                    (sum, item) => sum + item.price,
                    0
                );
                const bundlePrice = bundlePrices["online-bundle"];
                const onlineDiscount = onlineSubtotal - bundlePrice;
                if (onlineDiscount > 0) {
                    discount += onlineDiscount;
                    const onlinePercent = Math.round(
                        (onlineDiscount / onlineSubtotal) * 100
                    );
                    discountInfo = discountInfo
                        ? `${discountInfo} + ${onlinePercent}% popust - Sve online edukacije`
                        : `${onlinePercent}% popust - Sve online edukacije`;
                }
            }

            const total = subtotal - discount;

            return { subtotal, discount, total, discountInfo, discountPercent };
        }, [cartItems, bundlePrices]);

    return (
        <div className='page'>
            <div className='cart-hero-wrapper'>
                <div className='cart-hero-content'>
                    {cartItems && cartItems.length > 0 ? (
                        <>
                            <h1>Vaša</h1>
                            <h1>Korpa</h1>
                        </>
                    ) : (
                        <>
                            <h1>Vaša korpa</h1>
                            <h1>je prazna</h1>
                        </>
                    )}
                </div>
            </div>
            <div className='items'>
                <div className='cart-items-grid'>
                    {cartItems && cartItems.length > 0 ? (
                        cartItems.map((item) => {
                            const appointments = needsAppointment(
                                item.category,
                                item.id
                            )
                                ? educationSlots[item.id] || []
                                : null;
                            const selectedSlot = selectedSlots[item.id];
                            const needsSlotSelection =
                                appointments &&
                                appointments.length > 0 &&
                                !selectedSlot &&
                                showValidation;

                            const requiresStartDate = needsStartDate(
                                item.category,
                                item.id
                            );
                            const selectedStartDate =
                                selectedStartDates[item.id];
                            const needsDateSelection =
                                requiresStartDate &&
                                !selectedStartDate &&
                                showValidation;

                            return (
                                <div
                                    key={item.id}
                                    className={`cart-card ${
                                        needsSlotSelection || needsDateSelection
                                            ? "needs-appointment"
                                            : ""
                                    }`}
                                >
                                    <div className='cart-card-image'>
                                        <img
                                            src={getEducationImage(item.id)}
                                            alt={item.name}
                                        />
                                    </div>
                                    <div className='cart-card-content'>
                                        <div className='cart-card-header'>
                                            <h3 className='cart-card-title'>
                                                {item.name}
                                            </h3>
                                            <p className='cart-card-price'>
                                                €{item.price}
                                            </p>
                                        </div>
                                        <div className='cart-card-actions'>
                                            <button
                                                className='remove-btn'
                                                onClick={() =>
                                                    removeFromCart(item.id)
                                                }
                                            >
                                                Ukloni
                                            </button>
                                        </div>
                                    </div>

                                    {appointments && (
                                        <div className='appointment-picker'>
                                            <h4 className='appointment-title'>
                                                Izaberi termin
                                            </h4>
                                            {needsSlotSelection && (
                                                <p className='appointment-warning'>
                                                    ⚠ Molimo izaberite termin da
                                                    nastavite
                                                </p>
                                            )}
                                            {!selectedSlot ? (
                                                <div className='appointment-slots'>
                                                    {appointments.map(
                                                        (slot) => (
                                                            <div
                                                                key={slot.id}
                                                                className='appointment-slot-item'
                                                                onClick={() =>
                                                                    handleSlotSelect(
                                                                        item.id,
                                                                        slot.id
                                                                    )
                                                                }
                                                            >
                                                                <span className='slot-display'>
                                                                    {formatDateManual(
                                                                        slot.slotDate
                                                                    )}
                                                                    <span
                                                                        style={{
                                                                            padding:
                                                                                "0 4px",
                                                                        }}
                                                                    >
                                                                        {" "}
                                                                        -{" "}
                                                                    </span>
                                                                    <span className='slot-time-gold'>
                                                                        {slot.startTime.substring(
                                                                            0,
                                                                            5
                                                                        )}
                                                                        <span
                                                                            style={{
                                                                                padding:
                                                                                    "0 4px",
                                                                            }}
                                                                        >
                                                                            {" "}
                                                                            -{" "}
                                                                        </span>
                                                                        {slot.endTime.substring(
                                                                            0,
                                                                            5
                                                                        )}
                                                                    </span>
                                                                </span>
                                                                <span className='chevron'>
                                                                    ▲
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            ) : (
                                                <div className='appointment-slots'>
                                                    <div
                                                        className='appointment-slot-item selected'
                                                        onClick={() =>
                                                            handleSlotSelect(
                                                                item.id,
                                                                null
                                                            )
                                                        }
                                                    >
                                                        <span className='slot-display'>
                                                            {(() => {
                                                                const slot =
                                                                    appointments.find(
                                                                        (s) =>
                                                                            s.id ===
                                                                            selectedSlot
                                                                    );
                                                                return slot ? (
                                                                    <>
                                                                        {formatDateManual(
                                                                            slot.slotDate
                                                                        )}
                                                                        <span
                                                                            style={{
                                                                                padding:
                                                                                    "0 4px",
                                                                            }}
                                                                        >
                                                                            {" "}
                                                                            -{" "}
                                                                        </span>
                                                                        <span className='slot-time-gold'>
                                                                            {slot.startTime.substring(
                                                                                0,
                                                                                5
                                                                            )}
                                                                            <span
                                                                                style={{
                                                                                    padding:
                                                                                        "0 4px",
                                                                                }}
                                                                            >
                                                                                {" "}
                                                                                -{" "}
                                                                            </span>
                                                                            {slot.endTime.substring(
                                                                                0,
                                                                                5
                                                                            )}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    ""
                                                                );
                                                            })()}
                                                        </span>
                                                        <span className='chevron'>
                                                            ▼
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Date Picker for Beginner Educations */}
                                    {requiresStartDate && (
                                        <div className='appointment-picker'>
                                            <h4 className='appointment-title'>
                                                Odaberite datum održavanja
                                                edukacije
                                            </h4>
                                            {needsDateSelection && (
                                                <p className='appointment-warning'>
                                                    ⚠ Molimo izaberite datum
                                                    početka da nastavite
                                                </p>
                                            )}

                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                                adapterLocale='sr'
                                            >
                                                <DatePicker
                                                    label='Datum početka'
                                                    value={
                                                        selectedStartDate
                                                            ? dayjs(
                                                                  selectedStartDate
                                                              )
                                                            : null
                                                    }
                                                    onChange={(newValue) => {
                                                        if (newValue) {
                                                            handleStartDateSelect(
                                                                item.id,
                                                                newValue.format(
                                                                    "YYYY-MM-DD"
                                                                )
                                                            );
                                                        }
                                                    }}
                                                    minDate={getMinStartDate(
                                                        item.id
                                                    )}
                                                    shouldDisableDate={(
                                                        date
                                                    ) => {
                                                        return shouldDisableDate(
                                                            date,
                                                            item.id
                                                        );
                                                    }}
                                                    format='DD.MM.YYYY'
                                                    sx={{
                                                        width: "100%",
                                                        "& .MuiOutlinedInput-root":
                                                            {
                                                                backgroundColor:
                                                                    "#1e1e1e",
                                                                color: "#fff",
                                                                "& fieldset": {
                                                                    borderColor:
                                                                        "#be9455",
                                                                },
                                                                "&:hover fieldset":
                                                                    {
                                                                        borderColor:
                                                                            "#d4a866",
                                                                    },
                                                                "&.Mui-focused fieldset":
                                                                    {
                                                                        borderColor:
                                                                            "#be9455",
                                                                    },
                                                            },
                                                        "& .MuiInputLabel-root":
                                                            {
                                                                color: "#be9455",
                                                            },
                                                        "& .MuiSvgIcon-root": {
                                                            color: "#be9455",
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>

                                            <p
                                                style={{
                                                    fontSize: "16px",
                                                    color: "#be9455",
                                                    textAlign: "center",
                                                    fontStyle: "italic",
                                                    marginTop: "15px",
                                                    marginLeft: "20px",
                                                }}
                                            >
                                                O daljem dogovoru ćete biti
                                                obavešteni putem mejla ili broja
                                                telefona
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className='empty-cart'>
                            <img
                                src={Logo}
                                alt='Logo'
                                className='empty-cart-logo'
                            />
                        </div>
                    )}
                </div>

                {cartItems && cartItems.length > 0 && (
                    <>
                        <div className='cart-summary'>
                            <div className='summary-row'>
                                <span>Međuzbir:</span>
                                <span>€{subtotal.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                                <>
                                    <div className='summary-row discount-row'>
                                        <span>{discountInfo}</span>
                                        <span>-€{discount.toFixed(2)}</span>
                                    </div>
                                </>
                            )}
                            <div className='summary-row total-row'>
                                <span>Ukupno:</span>
                                <span>€{total.toFixed(2)}</span>
                            </div>
                            <button
                                className='checkout-btn'
                                onClick={() => {
                                    // Check if all items needing appointments have slots selected
                                    const hasUnselectedSlots = cartItems.some(
                                        (item) =>
                                            needsAppointment(
                                                item.category,
                                                item.id
                                            ) && !selectedSlots[item.id]
                                    );

                                    // Check if all items needing dates have start dates selected
                                    const hasUnselectedDates = cartItems.some(
                                        (item) =>
                                            needsStartDate(
                                                item.category,
                                                item.id
                                            ) && !selectedStartDates[item.id]
                                    );

                                    if (
                                        hasUnselectedSlots ||
                                        hasUnselectedDates
                                    ) {
                                        setShowValidation(true);
                                        window.scrollTo({
                                            top: 0,
                                            behavior: "smooth",
                                        });
                                    } else {
                                        // Save selected slots, start dates, and start times to localStorage for checkout
                                        localStorage.setItem(
                                            "selectedSlots",
                                            JSON.stringify(selectedSlots)
                                        );
                                        localStorage.setItem(
                                            "selectedStartDates",
                                            JSON.stringify(selectedStartDates)
                                        );
                                        navigate("/educations/checkout");
                                    }
                                }}
                            >
                                Nastavi dalje
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CartPage;
